/* eslint-disable no-loop-func */
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import store from './redux/store/AppStore';
import { SORT_MAP } from './redux/slices/leaderboardSlice';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

export const getUser = () => {
    const oidcStorage = localStorage.getItem(
        `oidc.user:${window.cognitoAuthConfig.authority}:${window.cognitoAuthConfig.client_id}`
    );
    if (!oidcStorage) {
        return null;
    }

    const user = User.fromStorageString(oidcStorage);
    return user;
};

// Standalone UserManager used only for on-demand silent renew from inside
// authFetch. Shares storage (and therefore the current user) with the
// AuthProvider's internal UserManager via the same localStorage key.
let _userManager = null;
const getUserManager = () => {
    if (_userManager) return _userManager;
    if (!window.cognitoAuthConfig) return null;
    _userManager = new UserManager({
        ...window.cognitoAuthConfig,
        userStore: new WebStorageStateStore({ store: localStorage }),
    });
    return _userManager;
};

// fetch wrapper that attaches the current Bearer token and transparently
// retries once on 401 after a silent renew. Callers own every other header
// (Content-type, etc.) — this helper only touches `authorization`.
export const authFetch = async (url, init = {}) => {
    const buildInit = (token) => {
        const headers = { ...(init.headers || {}) };
        if (token) {
            headers.authorization = `Bearer ${token}`;
        }
        return { ...init, headers };
    };

    const user = getUser();
    let response = await fetch(url, buildInit(user?.access_token));

    if (response.status === 401 && user) {
        const manager = getUserManager();
        if (manager) {
            try {
                const refreshed = await manager.signinSilent();
                if (refreshed?.access_token) {
                    response = await fetch(url, buildInit(refreshed.access_token));
                }
            } catch (e) {
                // Silent renew failed — propagate the original 401 to the caller.
            }
        }
    }

    return response;
};

// SecretHash = base64(HMAC-SHA256(username + clientId, clientSecret)).
// Required by Cognito's user-pool API whenever the app client has a secret
// configured — which ours does (exposed in index.jsx, but that ship sailed).
const computeSecretHash = async (username) => {
    const { client_id: clientId, client_secret: clientSecret } = window.cognitoAuthConfig;
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        enc.encode(clientSecret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', key, enc.encode(username + clientId));
    return btoa(String.fromCharCode(...new Uint8Array(sig)));
};

const callCognito = async (target, body) => {
    // authority is `https://cognito-idp.<region>.amazonaws.com/<userPoolId>`; the
    // user-pool API (ResendConfirmationCode, ConfirmSignUp, …) lives at the
    // region root, not the pool-qualified path — ClientId in the body
    // identifies the pool.
    const endpoint = new URL(window.cognitoAuthConfig.authority).origin + '/';
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
        },
        body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const err = new Error(data.message || `Cognito ${target} failed`);
        err.code = data.__type?.split('#').pop() || 'UnknownError';
        throw err;
    }
    return data;
};

export const resendConfirmationCode = async (email) => {
    const trimmed = email.trim().toLowerCase();
    const secretHash = await computeSecretHash(trimmed);
    return callCognito('ResendConfirmationCode', {
        ClientId: window.cognitoAuthConfig.client_id,
        Username: trimmed,
        SecretHash: secretHash,
    });
};

export const confirmSignUp = async (email, code) => {
    const trimmed = email.trim().toLowerCase();
    const secretHash = await computeSecretHash(trimmed);
    return callCognito('ConfirmSignUp', {
        ClientId: window.cognitoAuthConfig.client_id,
        Username: trimmed,
        ConfirmationCode: code.trim(),
        SecretHash: secretHash,
    });
};

export const getClientDomainPrefix = () => {
    const href = window.location.href;

    if (href.includes(`localhost`)) {
        return `http://localhost:3000`;
    } else if (href.includes(`192.168`)) {
        return `http://192.168.1.27:3000`;
    } else if (href.includes(`staging.commandersalt.com`)) {
        return `https://staging.commandersalt.com`;
    }

    return `https://commandersalt.com`;
};

export const getDomainPrefix = () => {
    const href = window.location.href;

    if (href.includes(`localhost`)) {
        return `http://localhost:3333`;
    } else if (href.includes(`192.168`)) {
        return `http://192.168.1.27:3333`;
    }

    if (href.includes('staging')) {
        return `https://api.commandersalt.com`;
    }

    return `https://api.commandersalt.com`;
};

export const DynamoConnector = {
    getUserDetails: async (id, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/details?id=${id}&isUser=true`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            toast(`Unable to find user`);
            callback(null);
        }
    },
    // Fetch the single-call user-stats envelope described in the
    // user-stats integration guide. Returns:
    //   { status, eligible, deckCount, lastUpdatedAt, rebuildGeneration,
    //     playerType, projection }
    // `id` accepts either the UUID ownerId or the username — the API
    // resolves both. Returns null on any error; the caller renders the
    // "still computing" placeholder.
    getUserCardStats: async (idOrUsername, callback) => {
        if (!idOrUsername) {
            callback && callback(null);
            return;
        }
        try {
            const fetchUri = `${getDomainPrefix()}/usercardstats?id=${encodeURIComponent(idOrUsername)}`;
            // Public endpoint — issue as a "simple" CORS request: no
            // Authorization header (preflight trigger) and no custom
            // Content-type (application/json also triggers preflight).
            // The response is JSON regardless; we parse it below.
            const response = await fetch(fetchUri, { method: 'GET' });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            callback && callback(data && typeof data === 'object' ? data : null);
        } catch (error) {
            callback && callback(null);
        }
    },
    deletePatreonLink: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon`;

            const results = await authFetch(fetchUri, {
                method: 'DELETE',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Patreon link deleted`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    postUpdateUserPreferences: async (payload = [], callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/user?preferences=${encodeURIComponent(JSON.stringify(payload))}`;

            await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            toast(`Preferences saved`, { type: 'success' });
            callback(null);
        } catch (error) {
            toast(`Failed to save preferences`);
            callback(null);
        }
    },
    uploadAvatar: async (file, size, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/uploadavatar`;
            const formData = new FormData();
            formData.append(
                'data',
                JSON.stringify({
                    name: file.name,
                    description: file.description,
                })
            );
            formData.append(`files`, file, file.name);

            const results = await authFetch(fetchUri, {
                method: 'POST',
                body: formData,
            });

            toast(`Avatar updated`, { type: 'success' });
            callback(results);
        } catch (error) {
            toast(`Failed to update avatar`);
            callback(null);
        }
    },
    getRefreshPatreonMemberships: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon`;

            const results = await authFetch(fetchUri, {
                method: 'GET',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Patreon data refreshed`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    validatePatreonCode: async (code, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon?code=${code}`;

            const results = await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    deleteAccountLink: async (list, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?list=${encodeURIComponent(JSON.stringify(list))}`;

            const results = await authFetch(fetchUri, {
                method: 'DELETE',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Linked account(s) deleted`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    postValidateLinkCode: async (provider, username, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?provider=${provider}&username=${username}`;

            const results = await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    postManagedDecksOp: async (operation, deckIds = [], callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/managedecks?operation=${operation}&list=${encodeURIComponent(
                JSON.stringify(deckIds)
            )}`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'POST',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getManagedDecks: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/managedecks`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getAccountLinkCode: async (provider, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?provider=${provider}`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getUserProfile: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/user`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();
            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getBatchRefreshAuthorDeckList: async (list, callback) => {
        const promises = [];
        list.forEach((item) => {
            let fetchUri = `${getDomainPrefix()}/details?id=${item.id}`;
            promises.push(
                fetch(fetchUri, {
                    method: 'GET',
                    headers: {
                        'Content-type': 'application/json;charset=UTF-8',
                    },
                })
            );
        });

        await Promise.all(promises);
        callback && callback();
    },
    getStats: async (callback) => {
        let fetchUri = `${getDomainPrefix()}/stats`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    getDeck: async (id, callback, errorCallback) => {
        try {
            const startTime = performance.now();

            const deck = store.getState().preview.cache?.[id];
            if (deck) {
                callback(deck);

                // bail early
                return;
            }

            let fetchUri = `${getDomainPrefix()}/decks?id=${id}`;

            let results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            const endTime = performance.now();
            const responseTime = endTime - startTime;

            try {
                let _newJson = null;
                let strJson = JSON.stringify(results);

                strJson = strJson.replace(/\._/g, `__`);

                _newJson = JSON.parse(strJson);

                results = _newJson;
            } catch (error) {
                console.log(`crap`, error);
            }

            callback && callback(results);
        } catch (error) {
            toast(`Unable to find deck`);
            callback(null);
        }
    },
    getCards: async (deckId, callback, errorCallback) => {
        try {
            const fetchUri = `${getDomainPrefix()}/cards?deckId=${deckId}`;
            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${response.status}`);
            }
            const results = await response.json();
            callback && callback(results);
        } catch (error) {
            if (errorCallback) {
                errorCallback(error);
            } else {
                toast(`Unable to load card data`);
            }
        }
    },
    /**
     * Fetch live pricing for a card from Scryfall. Our own `price` field
     * is refreshed infrequently; Scryfall updates daily and is authoritative
     * for USD/EUR/foil/etched variants. Returns the `prices` object
     * ({ usd, usd_foil, usd_etched, eur, eur_foil, tix }) or null on error.
     *
     * Uses the `?exact=` name lookup since it's single-request, cacheable,
     * and doesn't require Scryfall's internal card UUID.
     *
     * See https://scryfall.com/docs/api/cards/named for endpoint details.
     */
    getScryfallPrice: async (cardName, callback) => {
        if (!cardName) {
            callback && callback(null);
            return;
        }
        try {
            const encoded = encodeURIComponent(cardName);
            const response = await fetch(`https://api.scryfall.com/cards/named?exact=${encoded}`, {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            callback && callback(data?.prices || null);
        } catch (error) {
            callback && callback(null);
        }
    },
    /**
     * Fetch every printing of a given card name from Scryfall so the card
     * dialog can list prices per print (set, collector number, release date,
     * finish, rarity, USD / USD-foil / USD-etched).
     *
     * Uses `cards/search?q=!"<name>"&unique=prints&order=released` — the
     * bang-exact syntax keeps false positives out (e.g. no matching
     * `Rhystic Study // Token` variants).
     */
    getScryfallPrints: async (cardName, callback) => {
        if (!cardName) {
            callback && callback(null);
            return;
        }
        try {
            const q = encodeURIComponent(`!"${cardName}"`);
            const uri = `https://api.scryfall.com/cards/search?q=${q}&unique=prints&order=released`;
            const response = await fetch(uri, {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            const prints = Array.isArray(data?.data)
                ? data.data.map((c) => ({
                      id: c.id,
                      set: c.set,
                      setName: c.set_name,
                      collectorNumber: c.collector_number,
                      releasedAt: c.released_at,
                      prices: c.prices || {},
                      rarity: c.rarity,
                      finishes: c.finishes || [],
                      promo: c.promo,
                      frameEffects: c.frame_effects || [],
                      scryfallUri: c.scryfall_uri,
                      imageUri: c.image_uris?.small || c.image_uris?.normal || null,
                  }))
                : [];
            callback && callback(prints);
        } catch (error) {
            callback && callback(null);
        }
    },
    getCardDetails: async (id, callback) => {
        let fetchUri = `${getDomainPrefix()}/details?id=${id?.replace(/-/g, '_')}&isCard=true`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    getCommanderDetails: async (id, callback) => {
        const canonicalId = id?.replace(/-/g, '_');
        const fetchUri = `${getDomainPrefix()}/details?id=${canonicalId}&isCommander=true`;

        const response = await fetch(fetchUri, {
            method: 'GET',
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        });

        // 400 → malformed id, 404 → unknown commander. Surface both as an
        // explicit error shape so the caller can toast + redirect instead of
        // rendering a zero-stub record (the pre-cleanup API used to return
        // that for any id, which hid real 404s).
        if (response.status === 400 || response.status === 404) {
            const body = await response.json().catch(() => ({}));
            callback({ error: body?.error || 'commander not found', status: response.status, id: canonicalId });
            return;
        }

        const results = await response.json();
        callback(results);
    },
    getCommanderStatsHealth: async (id, callback) => {
        try {
            const normalizedId = id?.replace(/-/g, '_');
            const fetchUri = `${getDomainPrefix()}/commandercardstats?id=${normalizedId}&mode=health`;

            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === 503) {
                callback({ disabled: true, data: null });
                return;
            }

            if (!response.ok) {
                callback({ disabled: false, data: null, badRequest: response.status === 400 });
                return;
            }

            const data = await response.json();
            callback({ disabled: false, data });
        } catch (error) {
            callback({ disabled: false, data: null });
        }
    },
    getCommanderCardStats: async (id, binKey, callback) => {
        try {
            const normalizedId = id?.replace(/-/g, '_');
            let binParam = '';
            if (binKey?.startsWith('bracket_')) {
                binParam = `bracket=${binKey.substring('bracket_'.length)}`;
            } else if (binKey?.startsWith('power_')) {
                binParam = `power=${binKey.substring('power_'.length)}`;
            } else {
                callback({ disabled: false, data: null, badRequest: true });
                return;
            }

            const fetchUri = `${getDomainPrefix()}/commandercardstats?id=${normalizedId}&${binParam}`;

            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === 503) {
                callback({ disabled: true, data: null });
                return;
            }

            if (!response.ok) {
                callback({ disabled: false, data: null, badRequest: response.status === 400 });
                return;
            }

            const data = await response.json();
            callback({ disabled: false, data });
        } catch (error) {
            callback({ disabled: false, data: null });
        }
    },
    getAuthorDetails: async (id, callback) => {
        const fetchUri = `${getDomainPrefix()}/details?id=${id}&isAuthor=true`;

        const response = await fetch(fetchUri, {
            method: 'GET',
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        });

        if (response.status === 400 || response.status === 404) {
            const body = await response.json().catch(() => ({}));
            callback({ error: body?.error || 'author not found', status: response.status, id });
            return;
        }

        const results = await response.json();
        callback(results);
    },
    getCEDHMeta: async (callback) => {
        let fetchUri = `${getDomainPrefix()}/meta`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    getDeckUpgrades: async (
        id,
        category,
        allowReservedList = true,
        colorFilters = null,
        selectedTypes = [],
        cursor,
        callback
    ) => {
        let fetchUri = `${getDomainPrefix()}/upgrades?id=${id}&category=${category}&allowReservedList=${allowReservedList}`;

        if (colorFilters) {
            fetchUri = `${fetchUri}&colorFilters=${colorFilters}`;
        }

        if (selectedTypes.length > 0) {
            fetchUri = `${fetchUri}&selectedTypes=${JSON.stringify(selectedTypes)}`;
        }

        if (cursor) {
            cursor = encodeURIComponent(JSON.stringify(cursor));
            fetchUri = `${fetchUri}&cursor=${cursor}`;
        }

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    /**
     * Generic variant of `getDeckUpgrades` for the source-based `/upgrades`
     * API (see `upgrades-api-additions.md`). Accepts a flat params object
     * and encodes everything appropriately:
     *   - `selectedTypes` (array) and `cursor` (object) are JSON-stringified
     *   - every other value is URL-encoded as a string
     *   - null / undefined / empty-array values are dropped
     *
     * The caller owns which params are legal for each `source`; this is
     * just a transport wrapper.
     */
    getDeckUpgradesExtended: async (params = {}, callback) => {
        const qs = [];
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (value == null) return;
            if (Array.isArray(value)) {
                if (value.length === 0) return;
                qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
                return;
            }
            if (typeof value === 'object') {
                qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
                return;
            }
            if (typeof value === 'string' && value.length === 0) return;
            qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        });
        const fetchUri = `${getDomainPrefix()}/upgrades?${qs.join('&')}`;

        try {
            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                callback({
                    error: body?.error || `HTTP ${response.status}`,
                    status: response.status,
                });
                return;
            }
            callback(body);
        } catch (err) {
            callback({ error: err?.message || 'network error', status: 0 });
        }
    },
    getList: async (cursor, category, index = null, filters, direction = SORT_MAP.DIRECTION.DESC, callback) => {
        let fetchUri = `${getDomainPrefix()}/list?category=${category}`;

        if (index === null) {
            index = SORT_MAP.POWERLEVEL.index;
        }

        // console.log(`=> ${JSON.stringify(direction)}`);
        const directionPrefix = direction === SORT_MAP.DIRECTION.DESC ? '' : '-';

        fetchUri = `${fetchUri}&sort=${directionPrefix}${index}`;

        if (cursor) {
            cursor = encodeURIComponent(JSON.stringify(cursor));
            fetchUri = `${fetchUri}&cursor=${cursor}`;
        }

        if (filters) {
            if (filters?.colorFilters) {
                fetchUri = `${fetchUri}&colorIdentity=${filters.colorFilters}`;
            }

            if (filters?.range) {
                fetchUri = `${fetchUri}&high=${filters.range.high}&low=${filters.range.low}`;
            }

            if (filters?.query && filters?.queryType) {
                fetchUri = `${fetchUri}&query=${filters.query}&queryType=${filters.queryType}`;
            }

            if (filters?.id) {
                fetchUri = `${fetchUri}&id=${filters.id}`;
            }

            if (filters.archetypes) {
                fetchUri = `${fetchUri}&archetypes=${filters.archetypes}`;
            }
        }

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        let sortKey = null;
        switch (index) {
            case 'salt':
                sortKey = SORT_MAP.SALT.field;
                break;
            case 'bracket':
                sortKey = SORT_MAP.BRACKET.field;
                break;
            default:
                sortKey = SORT_MAP.POWERLEVEL.field;
        }

        if (sortKey) {
            results.Items = results.Items.sort((a, b) => {
                if (direction === 'desc') {
                    if (a[sortKey] < b[sortKey]) {
                        return 1;
                    }

                    return -1;
                }

                if (a[sortKey] > b[sortKey]) {
                    return 1;
                }

                return -1;
            });
        }

        callback(results);
    },
    getShallowList: async (query, category, index = null, callback) => {
        let fetchUri = `${getDomainPrefix()}/list/shallow?category=${category}&id=${query}`;

        if (index) {
            fetchUri = `${fetchUri}&index=${index}`;
        }

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    addAuthorToPod: async (authorId, podId, doneCallback, errorCallback) => {
        const analytics = getAnalytics();

        try {
            const request = await fetch(`${getDomainPrefix()}/pods/authors/add?podId=${podId}&authorId=${authorId}`, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });
            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }

            doneCallback({
                ...response?.pod,
                key: response?.pod?.id,
            });
        } catch (error) {
            errorCallback(error);
            toast(`${error.message}`);
        }
    },
    importDeckList: async (url, statusCallback, doneCallback, errorCallback, isRefresh = false) => {
        const analytics = getAnalytics();

        let oldDeckId = 'sdlfksdf';
        if (window.location.pathname.includes(`/details/deck/`)) {
            oldDeckId = window.location.pathname.substring(window.location.pathname.indexOf(`/deck/`) + 6);
        }

        try {
            statusCallback({
                type: `card`,
                card: 'Loading...',
                percentage: 1,
            });

            let requestUri = `${getDomainPrefix()}/decks?url=${url}`;
            if (oldDeckId && isRefresh) {
                requestUri = `${requestUri}&oldDeckId=${oldDeckId}`;
            }

            const request = await authFetch(requestUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }

            doneCallback({
                ...response,
                key: response.id,
            });
        } catch (error) {
            errorCallback(error);
            toast(`${error.message}`);
        }
    },
    postContactPing: async (payload, callback) => {
        const analytics = getAnalytics();

        try {
            const email = encodeURIComponent(payload.email);
            const name = encodeURIComponent(payload.name);
            const message = encodeURIComponent(payload.message);

            const request = await fetch(`${getDomainPrefix()}/contact?email=${email}&name=${name}&message=${message}`, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }
        } catch (error) {
            callback(); // TODO: add status to ui
            logEvent(analytics, 'email_failed', { payload });
        }

        logEvent(analytics, 'email_success', { payload });
        callback();
    },
};
